// Helper functions for VRM expression control with smooth ramping animation
// This provides a clean API for controlling VRM expressions with gradual transitions
// instead of instant value changes for more natural-looking animations

export interface VRMExpressionManager {
  setValue: (expressionName: string, weight: number) => void;
  getValue: (expressionName: string) => number;
  update: (delta: number) => void;
  expressionMap: Record<string, any>;
}

export interface VRMExpressionController {
  setExpression: (avatarIndex: number, expressionName: string, weight: number, duration?: number) => boolean;
  resetExpression: (avatarIndex: number, expressionName: string, duration?: number) => boolean;
  resetAllExpressions: (avatarIndex: number, duration?: number) => boolean;
  getAvailableExpressions: (avatarIndex: number) => string[];
  getCurrentExpressionValue: (avatarIndex: number, expressionName: string) => number;
  setExpressionImmediate: (avatarIndex: number, expressionName: string, weight: number) => boolean;
}

class VRMExpressionControllerImpl implements VRMExpressionController {
  private animationTargets = new Map<string, { target: number; startValue: number; startTime: number; duration: number }>();
  private animationFrame: number | null = null;
  private readonly DEFAULT_DURATION = 300; // Default animation duration in ms

  private getExpressionManager(avatarIndex: number): VRMExpressionManager | null {
    try {
      const debugRef = (window as any).debugDEAIRef;
      return debugRef?.current?._webVision?._avatars?.[avatarIndex]?.currentVrm?.expressionManager || null;
    } catch (e) {
      console.error('Failed to get expression manager:', e);
      return null;
    }
  }

  private getAnimationKey(avatarIndex: number, expressionName: string): string {
    return `${avatarIndex}:${expressionName}`;
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  private startAnimation() {
    if (this.animationFrame !== null) return;

    const animate = (currentTime: number) => {
      let hasActiveAnimations = false;

      for (const [key, animation] of this.animationTargets.entries()) {
        const [avatarIndex, expressionName] = key.split(':');
        const manager = this.getExpressionManager(parseInt(avatarIndex));
        
        if (!manager) {
          this.animationTargets.delete(key);
          continue;
        }

        const elapsed = currentTime - animation.startTime;
        const progress = Math.min(elapsed / animation.duration, 1);
        const easedProgress = this.easeInOutCubic(progress);
        
        const currentValue = animation.startValue + (animation.target - animation.startValue) * easedProgress;
        
        try {
          manager.setValue(expressionName, currentValue);
        } catch (e) {
          console.error(`Failed to animate expression ${expressionName}:`, e);
          this.animationTargets.delete(key);
          continue;
        }

        if (progress >= 1) {
          this.animationTargets.delete(key);
          console.log(`✅ Animation complete: ${expressionName} = ${animation.target} for avatar ${avatarIndex}`);
        } else {
          hasActiveAnimations = true;
        }
      }

      if (hasActiveAnimations) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.animationFrame = null;
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  setExpression(avatarIndex: number, expressionName: string, weight: number, duration: number = this.DEFAULT_DURATION): boolean {
    const manager = this.getExpressionManager(avatarIndex);
    if (!manager) {
      console.warn(`No expression manager found for avatar ${avatarIndex}`);
      return false;
    }

    const key = this.getAnimationKey(avatarIndex, expressionName);
    const currentValue = this.getCurrentExpressionValue(avatarIndex, expressionName);
    
    // If target is the same as current value, no need to animate
    if (Math.abs(currentValue - weight) < 0.001) {
      return true;
    }

    this.animationTargets.set(key, {
      target: weight,
      startValue: currentValue,
      startTime: performance.now(),
      duration: duration
    });

    this.startAnimation();
    console.log(`🎬 Starting animation: ${expressionName} ${currentValue} → ${weight} over ${duration}ms for avatar ${avatarIndex}`);
    return true;
  }

  setExpressionImmediate(avatarIndex: number, expressionName: string, weight: number): boolean {
    const manager = this.getExpressionManager(avatarIndex);
    if (!manager) {
      console.warn(`No expression manager found for avatar ${avatarIndex}`);
      return false;
    }

    // Cancel any ongoing animation for this expression
    const key = this.getAnimationKey(avatarIndex, expressionName);
    this.animationTargets.delete(key);

    try {
      manager.setValue(expressionName, weight);
      console.log(`⚡ Set immediate ${expressionName} = ${weight} for avatar ${avatarIndex}`);
      return true;
    } catch (e) {
      console.error(`Failed to set expression ${expressionName}:`, e);
      return false;
    }
  }

  resetExpression(avatarIndex: number, expressionName: string, duration: number = this.DEFAULT_DURATION): boolean {
    return this.setExpression(avatarIndex, expressionName, 0.0, duration);
  }

  resetAllExpressions(avatarIndex: number, duration: number = this.DEFAULT_DURATION): boolean {
    const expressions = this.getAvailableExpressions(avatarIndex);
    let success = true;
    
    expressions.forEach(expr => {
      if (!this.resetExpression(avatarIndex, expr, duration)) {
        success = false;
      }
    });
    
    return success;
  }

  getAvailableExpressions(avatarIndex: number): string[] {
    const manager = this.getExpressionManager(avatarIndex);
    if (!manager?.expressionMap) {
      return [];
    }
    
    return Object.keys(manager.expressionMap);
  }

  getCurrentExpressionValue(avatarIndex: number, expressionName: string): number {
    const manager = this.getExpressionManager(avatarIndex);
    if (!manager) {
      return 0;
    }

    try {
      return manager.getValue?.(expressionName) || 0;
    } catch (e) {
      console.error(`Failed to get expression value for ${expressionName}:`, e);
      return 0;
    }
  }
}

// Export singleton instance
export const vrmExpressionController = new VRMExpressionControllerImpl();

// Export commonly used expressions for easy access
export const COMMON_EXPRESSIONS = {
  // Basic emotions
  HAPPY: 'happy',
  SAD: 'sad',
  ANGRY: 'angry',
  NEUTRAL: 'neutral',
  RELAXED: 'relaxed',
  
  // Eye expressions
  BLINK: 'blink',
  BLINK_LEFT: 'blinkLeft',
  BLINK_RIGHT: 'blinkRight',
  EYE_ANGRY: 'EYE_Angry',
  EYE_SHY: 'EYE_Shy',
  EYE_SMUG: 'EYE_Smug',
  EYE_YANDERE: 'EYE_Yandere',
  EYE_FLOWERY: 'EYE_Flowery',
  
  // Full face expressions  
  FULL_SMUG: 'FULL_Smug',
  FULL_DISGUST: 'FULL_Disgust',
  FULL_SCARED: 'FULL_Scared',
  
  // Look directions
  LOOK_UP: 'lookUp',
  LOOK_DOWN: 'lookDown',
  LOOK_LEFT: 'lookLeft',
  LOOK_RIGHT: 'lookRight',
  
  // Speech shapes
  MOUTH_AA: 'aa',
  MOUTH_IH: 'ih',
  MOUTH_OU: 'ou',
  MOUTH_EE: 'ee',
  MOUTH_OH: 'oh'
} as const;

// Utility functions for common expression patterns
export const expressionPatterns = {
  // Emotional expressions
  showHappiness: (avatarIndex = 0, intensity = 1.0, duration = 300) => {
    vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.HAPPY, intensity, duration);
  },
  
  showSadness: (avatarIndex = 0, intensity = 1.0, duration = 300) => {
    vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.SAD, intensity, duration);
  },
  
  showAnger: (avatarIndex = 0, intensity = 1.0, duration = 300) => {
    vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.ANGRY, intensity, duration);
  },
  
  // Eye expressions
  blink: (avatarIndex = 0) => {
    vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.BLINK, 1.0, 50);
    setTimeout(() => {
      vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.BLINK, 0.0, 100);
    },150);
  },
  
  wink: (avatarIndex = 0, leftEye = true) => {
    const eyeExpression = leftEye ? COMMON_EXPRESSIONS.BLINK_LEFT : COMMON_EXPRESSIONS.BLINK_RIGHT;
    vrmExpressionController.setExpressionImmediate(avatarIndex, eyeExpression, 1.0);
    setTimeout(() => {
      vrmExpressionController.setExpressionImmediate(avatarIndex, eyeExpression, 0.0);
    }, 300);
  },
  
  // Look directions
  lookAt: (avatarIndex = 0, direction: 'up' | 'down' | 'left' | 'right', intensity = 1.0, duration = 300) => {
    const expressionMap = {
      up: COMMON_EXPRESSIONS.LOOK_UP,
      down: COMMON_EXPRESSIONS.LOOK_DOWN,
      left: COMMON_EXPRESSIONS.LOOK_LEFT,
      right: COMMON_EXPRESSIONS.LOOK_RIGHT
    };
    vrmExpressionController.setExpression(avatarIndex, expressionMap[direction], intensity, duration);
  },
  
  // Reset to neutral
  resetToNeutral: (avatarIndex = 0, duration = 500) => {
    vrmExpressionController.resetAllExpressions(avatarIndex, duration);
    vrmExpressionController.setExpression(avatarIndex, COMMON_EXPRESSIONS.NEUTRAL, 1.0, duration);
  }
};
